// src/components/Tasks/TaskForm.tsx
import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Button } from "@/components/Base/Button";
import { Checkbox } from "@/components/Base/Checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";
import { Textarea } from "@/components/Base/Textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import { Combobox } from "@/components/Base/Combobox";
import { Tabs, TabsList, TabsTrigger } from "@/components/Base/Tabs";
import { DateTimePickerForm } from "@/components/Common/form/DateTimePicker.Field";
import { Icon } from "@/components/Common/Icon";
import { AttachmentsMain } from "@/components/Common/Attachments/AttachmentsMain";
import { deleteField } from "firebase/firestore";
import { Card, IconNames, Task, TaskType, FEATURE_FLAGS } from "@/types/v2";
import { useStore } from "@/stores/useStore";
import { useTasks } from "@/hooks/store/useTasks";
import { useTasksAnalytics } from "@/hooks/useTasksAnalytics";
import { useFeature } from "@/hooks/useFeatures";
import { useRoles } from "@/hooks/useRoles";
import { taskAttachmentsPath, taskPath } from "@/repositories/attachment";
import { toast } from "@/hooks/useToast";
import { getServiceAdvisorColor, getEmployeeName } from "@/lib/employees";
import { TASK_TYPE_LABELS, TASK_TYPE_COLORS } from "@/lib/const";
import { cardsToTaskOptions } from "@/lib/taskFormOptions";
import {
  resolveDueDate,
  DueDateInput,
  DueDateUnit,
  DUE_DATE_UNITS,
} from "@/lib/taskDueDate";
import { firestoreToDateTime, capitalize } from "@/lib/utils";
import { taskFormSchema, TaskFormValues } from "./TaskForm.schema";

export interface TaskFormProps {
  task?: Task;
  initialRelatedRO?: Card["id"];
  initialValues?: Partial<TaskFormValues>;
  onSuccess?: () => void;
  onCancel: () => void;
}

const FIELD_LABEL_CLASS =
  "text-xs font-medium uppercase tracking-wide text-muted-foreground";

// 1, not 0: resolveDueDate(0) returns now, so taskStatus() classifies the task
// OVERDUE on the very next tick and it renders as overdue in every list and
// status filter the moment it is created. If the creator also picks an
// assignee, it additionally reaches selectMyOverdueTasks and raises the
// overdue toast (WEBV3-120). Neither the toast nor the open-task badge fires
// for an *unassigned* task — both key off assigneeId — so the list-rendering
// case is the one that always applies.
const DEFAULT_RELATIVE_AMOUNT = 1;

function buildDefaultValues(
  task?: Task,
  initialRelatedRO?: Card["id"],
  initialValues?: Partial<TaskFormValues>
): TaskFormValues {
  // Creating: default to the relative tab seeded at 1 hour, so the required
  // due date is pre-satisfied without being born overdue. Editing: a saved
  // due date is an absolute instant, so it stays on the exact tab — rendering
  // it as "N hours after created" would be wrong, and re-resolving it on save
  // would walk the date forward on every edit.
  const isEditing = Boolean(task);
  // A caller that pre-fills only `dueDateExact` means the exact tab, even on
  // create. Hardcoding "relative" here would land them on the relative tab
  // with their date silently ignored and the task created an hour out — the
  // old unconditional "exact" default made partial initialValues safe, so
  // deriving the mode is what keeps them safe.
  const wantsExactDate = isEditing || initialValues?.dueDateExact !== undefined;
  return {
    title: task?.title ?? "",
    description: task?.description ?? "",
    assigneeId: task?.assigneeId ?? "none",
    type: task?.type ?? TaskType.GENERAL,
    relatedRO: task?.relatedRO ?? initialRelatedRO ?? "",
    isPrivate: task?.isPrivate ?? false,
    dueDateMode: wantsExactDate ? "exact" : "relative",
    dueDateExact: task?.dueDate ? firestoreToDateTime(task.dueDate) : undefined,
    dueDateRelativeAmount: isEditing ? undefined : DEFAULT_RELATIVE_AMOUNT,
    dueDateRelativeUnit: "hours",
    ...initialValues,
  };
}

export const TaskForm = ({
  task,
  initialRelatedRO,
  initialValues,
  onSuccess,
  onCancel,
}: TaskFormProps) => {
  const { cards, employees, shopId } = useStore();
  const { createTask, updateTask } = useTasks();
  const { logTaskCreated } = useTasksAnalytics();
  const isAttachmentsEnabled = useFeature(FEATURE_FLAGS.isAttachmentsEnabled);
  const { isViewOnly } = useRoles();

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: buildDefaultValues(task, initialRelatedRO, initialValues),
  });

  const dueDateMode = form.watch("dueDateMode");
  // cardsToTaskOptions re-maps and re-sorts every board card; without this,
  // it re-ran on every render this form triggers, including every keystroke
  // in the (unrelated) form fields.
  const relatedROOptions = useMemo(() => cardsToTaskOptions(cards), [cards]);

  const handleModeChange = (value: string) => {
    const mode = value as TaskFormValues["dueDateMode"];
    form.setValue("dueDateMode", mode);
    if (
      mode === "relative" &&
      form.getValues("dueDateRelativeAmount") === undefined
    ) {
      form.setValue("dueDateRelativeAmount", DEFAULT_RELATIVE_AMOUNT);
      form.setValue("dueDateRelativeUnit", "hours");
    }
  };

  const handleFormSubmit = async (values: TaskFormValues) => {
    // react-hook-form's zodResolver drops a Date-typed Controller field
    // (dueDateExact) from the `values` it hands to the submit callback even
    // though the field passed validation and is present in the live form
    // state — read it back via getValues() rather than trusting `values`
    // here, or an existing task's due date silently resolves to undefined
    // and Firestore's updateDoc() rejects the write.
    const dueDateInput: DueDateInput =
      values.dueDateMode === "exact"
        ? { mode: "exact", date: form.getValues("dueDateExact") as Date }
        : {
            mode: "relative",
            amount: values.dueDateRelativeAmount as number,
            unit: values.dueDateRelativeUnit as DueDateUnit,
          };
    const dueDate = resolveDueDate(dueDateInput);

    const resolvedAssigneeAuthUid =
      values.assigneeId && values.assigneeId !== "none"
        ? employees?.find((e) => e.id === values.assigneeId)?.shopHeroAuthUid
        : undefined;

    const basePatch: Partial<Task> = {
      title: values.title,
      type: values.type,
      dueDate,
      isPrivate: values.isPrivate,
    };

    try {
      if (task) {
        // Editing an existing task: updateDoc supports the deleteField()
        // sentinel, so a field the user clears is actually removed rather
        // than left at its previous value — omitting the key here would be
        // a silent no-op, since updateDoc only touches keys present in the
        // payload. `!values.assigneeId || ...` (not a strict `=== "none"`
        // check) mirrors the `||` guard on description/relatedRO below, so
        // an unexpected falsy assigneeId can't slip through as `undefined`.
        const updatePatch = {
          ...basePatch,
          description: values.description || deleteField(),
          assigneeId:
            !values.assigneeId || values.assigneeId === "none"
              ? deleteField()
              : values.assigneeId,
          assigneeAuthUid: resolvedAssigneeAuthUid ?? deleteField(),
          relatedRO: values.relatedRO || deleteField(),
        };
        // task.id is typed string | undefined via BaseFirestore, but a Task
        // passed in for editing always originated from a persisted Firestore
        // doc and therefore always has an id. deleteField() sentinels aren't
        // representable in Partial<Task>'s string-typed optional fields, so
        // the cast below is required the same way task.id's cast is.
        await updateTask(task.id as string, updatePatch as Partial<Task>);
      } else {
        // Creating a new task: setDoc (no merge) rejects `undefined` field
        // values outright, and rejects deleteField() too, so optional fields
        // are omitted entirely rather than assigned undefined. New tasks
        // must also explicitly set `completed` (a required field on Task).
        const createPatch: Partial<Task> = { ...basePatch, completed: false };
        if (values.description) {
          createPatch.description = values.description;
        }
        if (values.assigneeId && values.assigneeId !== "none") {
          createPatch.assigneeId = values.assigneeId;
          if (resolvedAssigneeAuthUid) {
            createPatch.assigneeAuthUid = resolvedAssigneeAuthUid;
          }
        }
        if (values.relatedRO) {
          createPatch.relatedRO = values.relatedRO;
        }
        await createTask(createPatch);
        logTaskCreated({
          dueDateMode: values.dueDateMode,
          hasAssignee: Boolean(
            values.assigneeId && values.assigneeId !== "none"
          ),
          hasRelatedRO: Boolean(values.relatedRO),
        });
      }
      onSuccess?.();
    } catch (error) {
      // Without this, a rejected updateTask/createTask (e.g. WEBV3-304's
      // private-tasks rule denying generic.update's read-back for a viewer
      // who just made a task private, or unassigned themselves from one)
      // surfaces as an unhandled promise rejection: the write can have
      // already succeeded, but the dialog is left open with no feedback and
      // no indication anything went wrong. Not calling onSuccess?.() here
      // keeps the dialog open deliberately, matching the codebase's existing
      // convention (see useTaskListActions.ts's handleToggleComplete) of
      // staying put on a failed mutation rather than pretending it worked.
      console.error("Failed to save task:", error);
      toast({
        title: task ? "Failed to save changes" : "Failed to create task",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={FIELD_LABEL_CLASS}>
                Title <span className="text-destructive">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Task title"
                  disabled={form.formState.isSubmitting}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={FIELD_LABEL_CLASS}>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Optional details"
                  disabled={form.formState.isSubmitting}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="assigneeId"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={FIELD_LABEL_CLASS}>Assignee</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={form.formState.isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Assignee" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {employees?.map((employee) => (
                        <SelectItem key={employee.id} value={employee.id}>
                          <span
                            className="mr-2 inline-block h-2.5 w-2.5 rounded-full"
                            style={{
                              backgroundColor: getServiceAdvisorColor(
                                employee.id,
                                employees
                              ),
                            }}
                          />
                          {getEmployeeName(employee.id, employees)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={FIELD_LABEL_CLASS}>Type</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={form.formState.isSubmitting}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.values(TaskType).map((type) => (
                        <SelectItem key={type} value={type}>
                          <span
                            className="mr-2 inline-block h-2.5 w-2.5 rounded-full"
                            style={{ backgroundColor: TASK_TYPE_COLORS[type] }}
                          />
                          {TASK_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="relatedRO"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={FIELD_LABEL_CLASS}>
                Related Repair Order
              </FormLabel>
              <FormControl>
                <div className="relative">
                  <Icon
                    name={IconNames.search}
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  />
                  <Combobox
                    options={relatedROOptions}
                    value={field.value}
                    onValueChange={field.onChange}
                    placeholder="Search ROs..."
                    searchPlaceholder="Search by RO # or customer..."
                    emptyText="No matching RO"
                    disabled={form.formState.isSubmitting}
                    className="w-full pl-9 font-normal"
                    popoverClassName="w-[var(--radix-popover-trigger-width)]"
                  />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="isPrivate"
          render={({ field }) => (
            <FormItem className="flex items-start gap-3 space-y-0 rounded-md border border-blue-300 bg-blue-100 p-3">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(val) => field.onChange(val === true)}
                  disabled={form.formState.isSubmitting}
                  className="mt-0.5 size-5 rounded-sm border-2 border-blue-600 bg-popover"
                />
              </FormControl>
              <FormLabel className="flex-1 space-y-0.5 font-normal leading-normal">
                <span className="flex items-center gap-1.5 text-sm font-semibold leading-none text-blue-950">
                  <Icon name={IconNames.lock} className="h-4 w-4 shrink-0" />
                  Private
                </span>
                <span className="block text-xs text-blue-950">
                  Visible only to you and the assignee
                </span>
              </FormLabel>
            </FormItem>
          )}
        />

        <FormItem>
          <FormLabel className={FIELD_LABEL_CLASS}>
            Due date <span className="text-destructive">*</span>
          </FormLabel>
          <Tabs value={dueDateMode} onValueChange={handleModeChange}>
            <TabsList variant="muted" className="w-full">
              <TabsTrigger
                value="exact"
                className="flex flex-1 items-center justify-center gap-1.5"
              >
                <Icon name={IconNames.calendar} className="h-4 w-4" />
                On a date/time
              </TabsTrigger>
              <TabsTrigger
                value="relative"
                className="flex flex-1 items-center justify-center gap-1.5"
              >
                <Icon name={IconNames.clock} className="h-4 w-4" />
                Relative to creation
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {dueDateMode === "exact" && (
            <DateTimePickerForm
              form={form}
              name="dueDateExact"
              placeholder="Pick a due date"
              disabled={form.formState.isSubmitting}
            />
          )}

          {dueDateMode === "relative" && (
            <div className="flex flex-row items-center gap-2">
              <FormField
                control={form.control}
                name="dueDateRelativeAmount"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        step={1}
                        className="w-20"
                        disabled={form.formState.isSubmitting}
                        value={field.value ?? ""}
                        onChange={(e) => {
                          // Convert to number here (not via zod's coerce)
                          // so `field.value` always matches TaskFormValues'
                          // inferred `number | undefined` type — passing a
                          // raw string through would fail type-check.
                          const raw = e.target.value;
                          field.onChange(raw === "" ? undefined : Number(raw));
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="dueDateRelativeUnit"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={form.formState.isSubmitting}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue placeholder="Unit" />
                        </SelectTrigger>
                        <SelectContent>
                          {DUE_DATE_UNITS.map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {capitalize(unit)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <span className="text-sm text-muted-foreground">
                after created
              </span>
            </div>
          )}
        </FormItem>

        {isAttachmentsEnabled && (
          <FormItem>
            <FormLabel className={FIELD_LABEL_CLASS}>Attachments</FormLabel>
            {task && shopId ? (
              <AttachmentsMain
                shopId={shopId}
                entityPath={taskPath(shopId, task.id as string)}
                attachmentsPath={taskAttachmentsPath(shopId, task.id as string)}
                canModify={!isViewOnly}
              />
            ) : (
              <p className="text-xs text-muted-foreground">
                Save the task first to attach files.
              </p>
            )}
          </FormItem>
        )}

        <div className="mt-2 flex flex-row justify-end gap-2 border-t pt-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={form.formState.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="accent"
            disabled={!form.formState.isValid || form.formState.isSubmitting}
          >
            {task ? "Save changes" : "Create task"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
